import { DatePipe } from "@angular/common";
import { Component, OnInit, Injector, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Cotista } from "src/app/model/cotista.model";
import { Subscricao } from "src/app/model/subscricao.model";
import { BaseEntityComponent, BnLoadingService, EntityPagedResult } from "@bndes/bndes-ux4/bndes-angular-utils";
import { ActivatedRoute, NavigationExtras } from '@angular/router';
import { MensagemService } from "src/app/features/mensagem.service";
import { PermissaoService } from "src/app/shared/permissao.service";
import { ROLE_GESTOR_CONSULTA, ROLE_GESTOR_ENVIO, ROLE_AGENTE_CONSULTA } from "src/app/constantes/permissao.constants";
import { LabelConglomeradoDicaComponent } from "../../conglomerado/label-conglomerado-dica/label-conglomerado-dica.component";
import { SubscricaoService } from "../../subscricao.service";

export interface SubscricaoDto {
  id?: number;
  data: Date;
  valor: number;
  categoria: string;
}

@Component({
    selector: 'app-edicao-subscricao-cota',
    templateUrl: './edicao-subscricao-cota.component.html',
    styleUrls: ['./edicao-subscricao-cota.component.scss'],
    standalone: false
})

export class EdicaoSubscricaoCotaComponent
  extends BaseEntityComponent<Subscricao> implements OnInit {

  @ViewChild(LabelConglomeradoDicaComponent) labelConglomeradoDicaComponent: LabelConglomeradoDicaComponent;
  cotistaSelecionado: Cotista;
  cotistaTemp: Cotista;
  idCotista: number;
  tipoAgente: string;
  categorias = [];
  subscricoes: Subscricao[] = [];
  totalSubscricoes: any;
  subscricao: Subscricao;
  edicao: boolean = false;
  formEdicaoSubscricao: FormGroup;
  dataHoje = new Date();
  podeEditarSubscricao: boolean = false;

  constructor(
    protected injector: Injector,
    formBuilder: FormBuilder,
    private subscricaoService: SubscricaoService,
    private bnLoadingService:BnLoadingService,
    private mensagemService: MensagemService,
    private permissaoService: PermissaoService,
    private datePipe: DatePipe) {
    super(subscricaoService);
    this.formEdicaoSubscricao = formBuilder.group({
      data: [{ value: "", disabled: true }, Validators.required],
      valor: [{ value: "", disabled: true }, [Validators.required, Validators.min(0.01)]],
      categoria: [{ value: "", disabled: true }, Validators.required],
    });
  }

  ngOnInit(): void {
    this.permissaoService.getPermissoesUsuarioAutenticado()
      .subscribe((resp: any) => {
          this.podeEditarSubscricao = resp && resp[ROLE_GESTOR_ENVIO];
      this.carregarDados();
    });
  }

  async carregarDados() {
    this.route.params.subscribe(params => { this.idCotista = params['id'] });
    this.route.params.subscribe(params => { this.tipoAgente = params['tipo'] });
    this.subscricaoService.getSubscricoesPorCotistaEModalidade(this.idCotista, this.tipoAgente)
      .toPromise().then(response => {
        this.subscricoes = response;
        this.cotistaTemp = response[0]?.cotista || this.cotistaTemp;
        this.cotistaSelecionado = response[0]?.cotista || this.cotistaTemp;
        this.totalSubscricoes = this.calcularValoresPorCategoria(response);
        this.bnLoadingService.liberaTela();
    });
    this.categorias = await this.subscricaoService.getCategorias().toPromise();

  }

  calcularValoresPorCategoria(subscricoes: Subscricao[]) {
    const totalPorCategoria: Record<string, number> = {};

    subscricoes.forEach(subscricao => {
      const total = Number(subscricao.valor);
      totalPorCategoria[subscricao.categoriaCota] = (totalPorCategoria[subscricao.categoriaCota] || 0) + total;
    });

    const listaTotalPorCategoria: { categoria: string; total: number }[] =
      Object.entries(totalPorCategoria).map(([categoria, total]) => ({
        categoria, total,
      }));

      return listaTotalPorCategoria.sort((a, b) => a.categoria.localeCompare(b.categoria));
  }

  voltar() {
    this.router.navigate(['/habilitacao/subscricao']);
  }

  btnEditarSubscricao(subscricao: Subscricao) {
    this.formEdicaoSubscricao.enable();
    const sub = subscricao;
    this.formEdicaoSubscricao.setValue({
      valor: sub.valor,
      categoria: sub.categoriaCota,
      data: this.ajustaCampoDate(subscricao?.dataSubscricao),
    });
    this.edicao = true;
    this.subscricao = subscricao;
  }

  cancelarEdicao() {
    this.edicao = false;
    this.subscricao = null;
    this.formEdicaoSubscricao.reset();
    this.formEdicaoSubscricao.disable();
  }

  async remover(id: number) {
    try {
      await this.subscricaoService.remover(id).toPromise();
      this.mensagemService.adicionarMensagemSucesso('Subscrição de Cota excluída com sucesso.');
      this.carregarDados();
    } catch (err) {
      console.log('erro ao remover subscrição', err);
      this.mensagemService.adicionarMensagemErro('Erro ao excluir subscrição de cota.');
    }
  }

  houveAlteracaoSubscricao(subscricao: Subscricao, edicao: SubscricaoDto): boolean {
    if (subscricao.categoriaCota !== edicao.categoria) {
      return true;
    }
    if (subscricao.valor !== edicao.valor) {
      return true;
    }

    const dataSub: Date = this.ajustaCampoDate(subscricao.dataSubscricao);
    const dataEd: Date = this.ajustaCampoDate(edicao.data);

    if (dataSub.getTime() !== dataEd.getTime()) {
      return true;
    }

    this.mensagemService.adicionarMensagemAviso('Não houve alteração na subscrição selecionada!');

    return false;
  }

  getSubscricaoEdicao(){
    const { data, valor, categoria } = this.formEdicaoSubscricao.value;
    const form = this.formEdicaoSubscricao.value;
    const subscricaoEdicao: SubscricaoDto = {
      id: this.subscricao.id,
      data: form.data,
      valor: form.valor,
      categoria: form.categoria,
    };
    return subscricaoEdicao;
  }

  async salvarEdicao() {
    if (this.formEdicaoSubscricao.invalid) {
      this.formEdicaoSubscricao.markAllAsTouched();
      return;
    }

    const subscricaoEdicao = this.getSubscricaoEdicao();

    if(!this.houveAlteracaoSubscricao(this.subscricao, subscricaoEdicao)){
      return;
    }

    try {
      await this.subscricaoService.editar(subscricaoEdicao).toPromise();
      this.mensagemService.adicionarMensagemSucesso('Subscrição de Cotas criadas/atualizadas com sucesso.');
      await this.carregarDados();
      this.cancelarEdicao();
      this.labelConglomeradoDicaComponent.carregarParticipantesConglomerado();
    } catch (err) {
      console.log('Erro ao editar subscrição!', err);
      this.mensagemService.adicionarMensagemErro('Erro ao editar subscrição!');
    }
  }

  ajustaCampoDate(data:any) {
    if (typeof data === 'string') {
      if (data.length == 10) {
        data += ' 00:00:00';
      }
      return new Date(data);
    }
    return data;
  }

}
